import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { nudgeApi } from '@/services/learningManagementApi';
import { skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Edit, Trash2, Bell, ExternalLink, Clock, Target, Zap } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Nudge } from '@/types/learningManagement';
import type { Skill } from '@/types/skillKnowledge';

export const NudgeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAdminAuth();
  const [skill, setSkill] = useState<Skill | null>(null);

  const canEdit = hasPermission('edit', 'nudge');
  const canDelete = hasPermission('delete', 'nudge');

  // Use entity hook for fetching nudge
  const {
    data: nudge,
    isLoading,
    error,
    refetch,
  } = useEntity<Nudge>({
    fetchFn: (nudgeId: string) => nudgeApi.getById(nudgeId),
    entityId: id,
    entityName: 'nudge',
    enabled: !!id,
  });

  // Fetch skill details if skillId exists
  useEffect(() => {
    if (!nudge?.skillId) {
      setSkill(null);
      return;
    }

    skillApi.getById(nudge.skillId)
      .then(setSkill)
      .catch((err) => {
        console.error('Error loading skill:', err);
        setSkill(null);
      });
  }, [nudge?.skillId]);

  const isSystemDefined = nudge?.nudgeGenerated === 'SYSTEM_DEFINED';
  const canModify = canEdit && (!isSystemDefined || hasPermission('system_admin', 'nudge'));

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Nudge ID is required');
      await nudgeApi.delete(id);
    },
    entityName: 'nudge',
    operation: 'delete',
    onSuccess: () => {
      window.location.href = '/admin/nudges';
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this nudge?')) return;
    await deleteMutation.mutate();
  };

  if (isLoading) {
    return <LoadingState message="Loading nudge..." />;
  }

  if (error || !nudge) {
    return <ErrorState error={error || new Error('Nudge not found')} onRetry={refetch} />;
  }

  const nudgeId = nudge.nudgeId || nudge.id || '';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ARCHIVED': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default: return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'HIGH': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'LOW': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/nudges">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Bell className="h-6 w-6 text-blue-600" />
              <h1 className="text-3xl font-bold tracking-tight">{nudge.title}</h1>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(nudge.status)}`}>
                {nudge.status}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(nudge.priority)}`}>
                {nudge.priority}
              </span>
              {isSystemDefined && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  System Defined
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {canModify && (
            <Button asChild>
              <Link to={`/admin/nudges/${nudgeId}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && !isSystemDefined && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Core nudge details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CopyableId id={nudgeId} label="Nudge ID" />
            {nudge.description && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Description</label>
                <p className="mt-1 text-sm">{nudge.description}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Content</label>
              <p className="mt-1 text-sm bg-muted/50 p-3 rounded-lg whitespace-pre-wrap">{nudge.content}</p>
            </div>
          </CardContent>
        </Card>

        {/* Classification */}
        <Card>
          <CardHeader>
            <CardTitle>Classification</CardTitle>
            <CardDescription>Category and type information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Category</label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-sm">
                    {nudge.category.replace(/_/g, ' ')}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Nudge Type</label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded text-sm">
                    {nudge.nudgeType.replace(/_/g, ' ')}
                  </span>
                </p>
              </div>
            </div>
            {nudge.subCategory && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Sub Category</label>
                <p className="mt-1 text-sm">{nudge.subCategory}</p>
              </div>
            )}
            {nudge.skillLevel && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Skill Level</label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 rounded text-sm">
                    {nudge.skillLevel}
                  </span>
                </p>
              </div>
            )}
            {nudge.deliveryFrequency && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Delivery Frequency</label>
                <p className="mt-1 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{nudge.deliveryFrequency.replace(/_/g, ' ')}</span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Related Skill */}
        {nudge.skillId && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Related Skill
              </CardTitle>
              <CardDescription>Skill associated with this nudge</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Link
                    to={`/admin/skills/${nudge.skillId}`}
                    className="font-medium text-blue-600 hover:underline flex items-center gap-2"
                  >
                    {skill?.name || 'View Skill'}
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
                <CopyableId id={nudge.skillId} label="Skill ID" />
                {skill?.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{skill.description}</p>
                )}
                {skill?.category && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs">
                    {skill.category.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delivery Settings */}
        {(nudge.deliveryChannels?.length || nudge.targetAudiences?.length) && (
          <Card>
            <CardHeader>
              <CardTitle>Delivery Settings</CardTitle>
              <CardDescription>How and to whom the nudge is delivered</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {nudge.deliveryChannels && nudge.deliveryChannels.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Delivery Channels</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {nudge.deliveryChannels.map((channel, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200 text-sm"
                      >
                        {channel.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {nudge.targetAudiences && nudge.targetAudiences.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Target Audiences</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {nudge.targetAudiences.map((audience, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200 text-sm"
                      >
                        {audience}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Audit Information */}
        <Card>
          <CardHeader>
            <CardTitle>Audit Information</CardTitle>
            <CardDescription>Creation and update history</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Created At</label>
                <p className="mt-1 text-sm">
                  {nudge.createdAt ? new Date(nudge.createdAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Updated At</label>
                <p className="mt-1 text-sm">
                  {nudge.updatedAt ? new Date(nudge.updatedAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              {nudge.createdBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created By</label>
                  <p className="mt-1 text-sm">{nudge.createdBy}</p>
                </div>
              )}
              {nudge.updatedBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Updated By</label>
                  <p className="mt-1 text-sm">{nudge.updatedBy}</p>
                </div>
              )}
              {nudge.version !== undefined && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Version</label>
                  <p className="mt-1 text-sm">{nudge.version}</p>
                </div>
              )}
              {nudge.nudgeGenerated && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Generated</label>
                  <p className="mt-1 text-sm">{nudge.nudgeGenerated.replace(/_/g, ' ')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Items */}
      {nudge.actionItems && nudge.actionItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Action Items ({nudge.actionItems.length})
            </CardTitle>
            <CardDescription>Suggested actions for this nudge</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              {nudge.actionItems.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 flex items-center justify-center text-sm font-medium">
                    {idx + 1}
                  </span>
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success Metrics */}
      {nudge.successMetrics && nudge.successMetrics.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Success Metrics ({nudge.successMetrics.length})</CardTitle>
            <CardDescription>How success is measured for this nudge</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              {nudge.successMetrics.map((metric, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 flex items-center justify-center text-sm font-medium">
                    ✓
                  </span>
                  <span className="text-sm">{metric}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trigger Conditions */}
      {nudge.triggerConditions && nudge.triggerConditions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Trigger Conditions ({nudge.triggerConditions.length})</CardTitle>
            <CardDescription>Conditions that trigger this nudge</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {nudge.triggerConditions.map((condition, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 rounded text-xs font-medium">
                      {condition.conditionType}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 rounded text-xs">
                      {condition.operator}
                    </span>
                  </div>
                  <p className="text-sm">
                    <span className="text-muted-foreground">Value:</span> {condition.value}
                  </p>
                  {condition.parameters && Object.keys(condition.parameters).length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Parameters: {JSON.stringify(condition.parameters)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tags */}
      {nudge.tags && nudge.tags.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tags ({nudge.tags.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {nudge.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NudgeDetailPage;
